import React, { useState, useEffect } from 'react';
import { X, FileText, Building, Calendar, MapPin, Clock, Package, CheckCircle, XCircle, AlertCircle, Loader, Check, Star } from 'lucide-react';
import Swal from "sweetalert2";

// Mock API_BASE_URL for demo
import API_BASE_URL from "../config";

const POApprovalModal = ({ po, isOpen, onClose, onApprove, onReject, getStatusBadge, formatDate }) => {
  const [poDetails, setPODetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [poApproval, setPOApproval] = useState(null);
  const [approverSignature, setApproverSignature] = useState(null);
  const [approverDetails, setApproverDetails] = useState(null);
  const [vendorName, setVendorName] = useState("");
  const [vendorDetails, setVendorDetails] = useState(null);
  const [currentRating, setCurrentRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isRatingLoading, setIsRatingLoading] = useState(false);
  const [canApprovePO, setCanApprovePO] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);

  useEffect(() => {
    if (isOpen && po) {
      fetchAllData();
    }
  }, [isOpen, po]);

  const fetchAllData = async () => {
    setLoading(true);
    setIsCheckingPermissions(true);
    try {
      await Promise.all([
        fetchPODetails(),
        fetchPOApproval(),
        fetchVendorName(),
        fetchCurrentRating(),
        checkPOApprovalPermission()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setIsCheckingPermissions(false);
    }
  };

  const fetchCurrentRating = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header/${po.id}/rate`);
      if (res.ok) {
        const data = await res.json();
        setCurrentRating(data.rate || 0);
      }
    } catch (error) {
      console.error('Error fetching rating:', error);
      setCurrentRating(0);
    }
  };

  const checkPOApprovalPermission = async () => {
    try {
      const userSession = localStorage.getItem("user");
      if (!userSession) {
        setCanApprovePO(false);
        return;
      }

      const sessionData = JSON.parse(userSession);
      const userLevelId = sessionData.userLevelId || sessionData.userLevel;

      const res = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
      if (!res.ok) throw new Error('Failed to fetch approver positions');

      const positions = await res.json();
      const userPosition = positions.find(pos => pos.approverUserLevelId === userLevelId);

      if (userPosition) {
        const canApprove = userPosition.po === 1;
        setCanApprovePO(canApprove);
      } else {
        setCanApprovePO(false);
      }
    } catch (error) {
      console.error('Error checking PO approval permission:', error);
      setCanApprovePO(false);
    }
  };

  const handleRateClick = async (rating) => {
    setIsRatingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header/${po.id}/rate`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate: rating })
      });

      if (res.ok) {
        setCurrentRating(rating);
        alert(`Rating saved: ${rating} stars`);
      } else {
        throw new Error('Failed to save rating');
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      alert('Failed to save rating');
    } finally {
      setIsRatingLoading(false);
    }
  };

  const fetchPODetails = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${po.id}`);
      const data = await res.json();
      setPODetails(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setPODetails([]);
    }
  };

  const fetchPOApproval = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Approval`);
      const data = await res.json();
      const approval = data.find(a => a.poHeaderId === po.id);

      if (approval) {
        setPOApproval(approval);
        if (approval.approverId) {
          await fetchUserSignature(approval.approverId);
          await fetchUserDetails(approval.approverId);
        }
      }
    } catch (error) {
      console.error('Error fetching PO approval:', error);
    }
  };

  const fetchVendorName = async () => {
    if (!po?.vendorId) return;
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor/${po.vendorId}`);
      const data = await res.json();
      setVendorName(data.VendorName || "N/A");
      setVendorDetails(data); // ✅ Store buong vendor data
    } catch (error) {
      console.error('Error fetching vendor:', error);
      setVendorName("N/A");
    }
  };

  const fetchUserSignature = async (userId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);
      if (res.ok) {
        const blob = await res.blob();
        const imageUrl = URL.createObjectURL(blob);
        setApproverSignature(imageUrl);
      }
    } catch (error) {
      console.error(`Error fetching signature for user ${userId}:`, error);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setApproverDetails(data);
      }
    } catch (error) {
      console.error(`Error fetching user details for ${userId}:`, error);
    }
  };

  useEffect(() => {
    return () => {
      if (approverSignature) {
        URL.revokeObjectURL(approverSignature);
      }
    };
  }, [approverSignature]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const calculateLineTotal = (qty, unitPrice, discount) => {
    const subtotal = qty * unitPrice;
    const discountAmount = (subtotal * (discount || 0)) / 100;
    return subtotal - discountAmount;
  };

  const calculateSubtotal = () => {
    return poDetails.reduce((sum, item) => {
      return sum + calculateLineTotal(item.qty, item.unitPrice, item.detailDiscount);
    }, 0);
  };

const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();  // after discount na
    const vat = calculateVAT();
    const headerDiscountAmount = (subtotal * (po.headerDiscount || 0)) / 100;
    return subtotal - headerDiscountAmount + vat;  // ← tanggalin na yung totalDiscount
};
  const calculateVAT = () => {
    if (po.isNonVAT) return 0;
    if (!po.isVatInclusive) return 0;
    return calculateSubtotal() * 0.12;
};

const calculateTotalDiscount = () => {
    return poDetails.reduce((sum, item) => {
        const lineTotal = item.qty * item.unitPrice;
        return sum + (lineTotal * (item.detailDiscount || 0)) / 100;
    }, 0);
};

  const handleApprove = async () => {
    const userSession = localStorage.getItem("user");

    if (!userSession) {
      await Swal.fire({ icon: "warning", title: "Session Expired", text: "No user session found. Please login again." });
      return;
    }

    const sessionData = JSON.parse(userSession);
    const currentUserId = sessionData.id || sessionData.userId;
    const userLevelId = sessionData.userLevelId || sessionData.userLevel;

    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Approval Confirmation",
      text: "Would you like to add remarks for this approval?",
      showCancelButton: true,
      confirmButtonText: "Yes, add remarks",
      cancelButtonText: "No, proceed",
    });

    let remarksText = "Approved via system";

    if (isConfirmed) {
      const { value } = await Swal.fire({
        title: "Approval Remarks",
        input: "textarea",
        inputPlaceholder: "Enter your remarks here...",
        showCancelButton: true,
        confirmButtonText: "Submit Approval",
      });
      if (!value) return;
      remarksText = value;
    }

    setIsSubmitting(true);

    try {
      const approvalData = {
        id: 0,
        poHeaderId: po.id,
        approverUserLevel: userLevelId || 1,
        response: 1,
        responseDateTime: new Date().toISOString(),
        responseRemarks: remarksText,
        approverId: currentUserId,
      };

      const approvalRes = await fetch(`${API_BASE_URL}api/T_PO_Approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(approvalData),
      });

      if (!approvalRes.ok) throw new Error("Failed to submit approval");

      const statusRes = await fetch(`${API_BASE_URL}api/T_PO_Header/update-status/${po.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(2),
      });

      if (!statusRes.ok) throw new Error("Failed to update PO status");

      const useQtyUpdates = poDetails
        .filter((item) => item.referencePRDetailsId)
        .map((item) => ({ prDetailId: item.referencePRDetailsId, qtyUsed: item.qty }));

      if (useQtyUpdates.length > 0) {
        await fetch(`${API_BASE_URL}api/PR_Details/update-useqty`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(useQtyUpdates),
        });
      }

      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "APPROVE",
          module: "Purchase Order",
          description: `Approved PO #${po.id}. Remarks: ${remarksText}`,
          userId: sessionData.id || sessionData.userId,
          username: sessionData.username,
          fullName: `${sessionData.firstName} ${sessionData.lastName}` || sessionData.username,
        }),
      }).catch((err) => console.error("Log failed:", err));

      await fetchPOApproval();
      if (onApprove) onApprove(po);

      await Swal.fire({
        icon: "success",
        title: "Purchase Order Approved",
        text: "The Purchase Order has been successfully approved.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error submitting approval:", error);
      await Swal.fire({ icon: "error", title: "Approval Failed", text: "An error occurred. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

const handleReject = async () => {
    const userSession = localStorage.getItem("user");
    if (!userSession) {
      await Swal.fire({ icon: "warning", title: "Session Expired", text: "No user session found. Please login again." });
      return;
    }

    const sessionData = JSON.parse(userSession);
    const currentUserId = sessionData.id || sessionData.userId;
    const userLevelId = sessionData.userLevelId || sessionData.userLevel;

    const { value: rejectionRemarks } = await Swal.fire({
      title: "Rejection Reason",
      input: "textarea",
      inputPlaceholder: "Please provide a reason for rejection...",
      showCancelButton: true,
      confirmButtonText: "Submit Rejection",
      inputValidator: (value) => { if (!value) return "You need to provide a reason!"; },
    });

    if (!rejectionRemarks) return;

    setIsSubmitting(true);
    try {
      const rejectionData = {
        id: 0,
        poHeaderId: po.id,
        approverUserLevel: userLevelId || 1,
        response: 0,
        responseDateTime: new Date().toISOString(),
        responseRemarks: rejectionRemarks,
        approverId: currentUserId,
      };

      const approvalRes = await fetch(`${API_BASE_URL}api/T_PO_Approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rejectionData),
      });

      if (!approvalRes.ok) throw new Error("Failed to submit rejection");

      const statusRes = await fetch(`${API_BASE_URL}api/T_PO_Header/update-status/${po.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(3),
      });

      if (!statusRes.ok) throw new Error("Failed to update PO status");

      // ✅ Only restore useQTY if the PO was previously APPROVED (status 2)
      // Pending POs (status 0 or 1) never updated useQTY, so restoring would cause negative/double deduction
      const wasApproved = po.status === 2 || po.status === '2';

      if (wasApproved) {
        const useQtyRestores = poDetails
          .filter((item) => item.referencePRDetailsId)
          .map((item) => ({ prDetailId: item.referencePRDetailsId, qtyUsed: -item.qty }));

        if (useQtyRestores.length > 0) {
          await fetch(`${API_BASE_URL}api/PR_Details/update-useqty`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(useQtyRestores),
          });
        }
      }

      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "REJECT",
          module: "Purchase Order",
          description: `Rejected PO #${po.id}. Reason: ${rejectionRemarks}`,
          userId: sessionData.id || sessionData.userId,
          username: sessionData.username,
          fullName: `${sessionData.firstName} ${sessionData.lastName}` || sessionData.username,
        }),
      }).catch((err) => console.error("Log failed:", err));

      await fetchPOApproval();
      if (onReject) onReject(po);

      await Swal.fire({
        icon: "success",
        title: "Purchase Order Rejected",
        text: wasApproved
          ? "The PO has been rejected and PR quantities have been restored."
          : "The PO has been rejected.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error submitting rejection:", error);
      await Swal.fire({ icon: "error", title: "Rejection Failed", text: "An error occurred. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPending = async () => {
    const userSession = localStorage.getItem("user");
    if (!userSession) {
      await Swal.fire({ icon: "warning", title: "Session Expired", text: "No user session found. Please login again." });
      return;
    }

    const sessionData = JSON.parse(userSession);
    const currentUserId = sessionData.id || sessionData.userId;
    const userLevelId = sessionData.userLevelId || sessionData.userLevel;

    const { value: pendingRemarks } = await Swal.fire({
      title: "Set to Pending",
      input: "textarea",
      inputPlaceholder: "Please provide a reason for setting this to pending...",
      showCancelButton: true,
      confirmButtonText: "Set to Pending",
      inputValidator: (value) => { if (!value) return "You need to provide a reason!"; },
    });

    if (!pendingRemarks) return;

    setIsSubmitting(true);
    try {
      const pendingData = {
        id: 0,
        poHeaderId: po.id,
        approverUserLevel: userLevelId || 1,
        response: -1,
        responseDateTime: new Date().toISOString(),
        responseRemarks: pendingRemarks,
        approverId: currentUserId,
      };

      const approvalRes = await fetch(`${API_BASE_URL}api/T_PO_Approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pendingData),
      });

      if (!approvalRes.ok) throw new Error("Failed to submit pending status");

      const statusRes = await fetch(`${API_BASE_URL}api/T_PO_Header/update-status/${po.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(1),
      });

      if (!statusRes.ok) throw new Error("Failed to update PO status");

      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "SET-PENDING",
          module: "Purchase Order",
          description: `Set PO #${po.id} to Pending. Reason: ${pendingRemarks}`,
          userId: sessionData.id || sessionData.userId,
          username: sessionData.username,
          fullName: `${sessionData.firstName} ${sessionData.lastName}` || sessionData.username,
        }),
      }).catch((err) => console.error("Log failed:", err));

      await fetchPOApproval();
      if (onReject) onReject(po);

      await Swal.fire({
        icon: "info",
        title: "Set to Pending",
        text: "The PO has been set to pending for review.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error setting to pending:", error);
      await Swal.fire({ icon: "error", title: "Failed", text: "An error occurred. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ✅ Status conditions
  const isPending = po.status === 0 || po.status === '0' || po.status === 1 || po.status === '1';
  const isApproved = po.status === 2 || po.status === '2' || (poApproval && poApproval.response === 1);
  const isRejected = po.status === 3 || po.status === '3' || (poApproval && poApproval.response === 0);
  const isPendingReview = poApproval && poApproval.response === -1;

  // ✅ Show action buttons only if pending AND not yet approved/rejected
  const showActionButtons = isPending && !isApproved && !isRejected;
  const isButtonDisabled = !isPending || isSubmitting || isCheckingPermissions || !canApprovePO;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-8 py-6 flex justify-between items-start sticky top-0 z-10">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8" />
              <div>
                <h3 className="text-2xl font-bold">PURCHASE ORDER</h3>
                <p className="text-blue-100 text-sm">Official Document</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div>
                <p className="text-xs text-blue-200 uppercase">PO Number</p>
                <p className="text-lg font-bold tracking-wide">{po.poNumber}</p>
              </div>
              <div className="h-10 w-px bg-blue-400"></div>
              <div>
                <p className="text-xs text-blue-200 uppercase">Status</p>
                <div className="mt-1">{getStatusBadge(po.status)}</div>
              </div>
              <div className="h-10 w-px bg-blue-400"></div>
              <div>
                <p className="text-xs text-blue-200 uppercase mb-1">Rate this PO</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => handleRateClick(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      disabled={isRatingLoading}
                      className={`transition-all duration-200 transform hover:scale-110 ${isRatingLoading ? 'cursor-wait opacity-50' : 'cursor-pointer'}`}
                    >
                      <Star className={`w-6 h-6 transition-colors ${star <= (hoverRating || currentRating) ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-blue-200'}`} />
                    </button>
                  ))}
                  {currentRating > 0 && (
                    <span className="ml-2 text-sm font-semibold bg-yellow-400 text-yellow-900 px-2 py-1 rounded">
                      {currentRating}/5
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition-all">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8">
          {/* Vendor + Delivery */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="border border-blue-100 rounded-xl overflow-hidden">
              <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-700">
                Vendor Information
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {[
                  ['Name', vendorDetails?.VendorName || vendorName || '—'],
                  ['Address', vendorDetails?.Address1 || '—'],
                  ['Address 2', vendorDetails?.Address2 || null],
                  ['Contact', vendorDetails?.ContactPerson || '—'],
                  ['Tel No', vendorDetails?.ContactNo || '—'],
                  ['TIN No', vendorDetails?.TIN || '—'],
                  ['Terms', vendorDetails?.PaymentTerms || po.terms || '—'],
                  ['Payment', po.paymentMode || '—'],
                ].filter(([, v]) => v !== null).map(([l, v]) => (
                  <div key={l} className="flex gap-2 text-xs">
                    <span className="text-slate-400 font-semibold w-16 shrink-0">{l}:</span>
                    <span className="text-slate-800 font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-emerald-100 rounded-xl overflow-hidden">
              <div className="bg-emerald-50 border-b border-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                Delivery Instruction
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {[
                  ['Ship To', po.shipTo_Location || '—'],
                  ['Address', po.shipTo_Address1 || '—'],
                  ['Contact', po.shipTo_ContactPerson || '—'],
                  ['Tel No', po.shipTo_ContactNo || '—'],
                ].map(([l, v]) => (
                  <div key={l} className="flex gap-2 text-xs">
                    <span className="text-slate-400 font-semibold w-16 shrink-0">{l}:</span>
                    <span className="text-slate-800 font-medium">{v}</span>
                  </div>
                ))}
                <div className="inline-block mt-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-lg">
                  Del Date: {formatDate(po.deliveryDate)}
                </div>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="flex justify-between items-start px-1 mb-6 text-xs">
            <div>
              <span className="font-bold uppercase tracking-widest text-slate-400">Bill To: </span>
              <span className="font-semibold text-slate-800">{po.shipTo_Location || '—'}</span>
            </div>
            <div className="text-right">
              <span className="font-bold uppercase tracking-widest text-slate-400">TIN No: </span>
              <span className="font-semibold text-slate-800">{po.tin || '—'}</span>
            </div>
          </div>

          {/* PO Items Table */}
          <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-8">
            <div className="bg-blue-600 text-white px-6 py-3 flex items-center gap-2">
              <Package className="w-5 h-5" />
              <h4 className="font-bold text-base uppercase">Purchase Order Items</h4>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
                <p className="text-gray-600 mt-3 text-sm">Loading items...</p>
              </div>
            ) : poDetails.length === 0 ? (
              <div className="text-center py-12 bg-gray-50">
                <p className="text-gray-500 text-sm">No items found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-300">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Item Description</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Qty</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">UOM</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Unit Price</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Discount %</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Line Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {poDetails.map((item, index) => (
                        <tr key={item.id || index} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-4 py-4 text-sm text-gray-700 font-bold">{index + 1}</td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-semibold text-gray-900">{item.itemDescription}</div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-center font-medium">{item.qty}</td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded">{item.uom}</span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right font-medium">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right font-medium">{item.detailDiscount || 0}%</td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-right font-bold">
                            {formatCurrency(calculateLineTotal(item.qty, item.unitPrice, item.detailDiscount))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

               <div className="bg-blue-50 border-t-4 border-blue-600 px-6 py-5">
  <div className="flex flex-col gap-3 items-end">
    <div className="flex justify-between items-center gap-8 w-full max-w-md">
      <span className="text-base font-bold text-gray-700 uppercase">Subtotal (VAT-Ex):</span>
      <span className="text-xl font-bold text-gray-800">{formatCurrency(calculateSubtotal())}</span>
    </div>
    {calculateTotalDiscount() > 0 && (
      <div className="flex justify-between items-center gap-8 w-full max-w-md">
        <span className="text-sm font-semibold text-red-600">Less Discount:</span>
        <span className="text-lg font-semibold text-red-600">-{formatCurrency(calculateTotalDiscount())}</span>
      </div>
    )}
    {po.headerDiscount > 0 && (
      <div className="flex justify-between items-center gap-8 w-full max-w-md">
        <span className="text-sm font-semibold text-red-600">Header Discount ({po.headerDiscount}%):</span>
        <span className="text-lg font-semibold text-red-600">-{formatCurrency((calculateSubtotal() * po.headerDiscount) / 100)}</span>
      </div>
    )}
    {calculateVAT() > 0 && (
      <div className="flex justify-between items-center gap-8 w-full max-w-md">
        <span className="text-sm font-semibold text-blue-600">VAT (12%):</span>
        <span className="text-lg font-semibold text-blue-600">+{formatCurrency(calculateVAT())}</span>
      </div>
    )}
    <div className="flex justify-between items-center gap-8 w-full max-w-md border-t-2 border-blue-300 pt-3">
      <span className="text-lg font-bold text-gray-800 uppercase tracking-wide">Grand Total:</span>
      <span className="text-2xl font-bold text-blue-700">{formatCurrency(calculateGrandTotal())}</span>
    </div>
  </div>
</div>
              </>
            )}
          </div>

          {/* Approval Status Section */}
          {poApproval && (
            <div className={`border-2 rounded-lg p-6 mb-8 ${
              isApproved ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' :
              isRejected ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300' :
              isPendingReview ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300' :
              'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-300'
            }`}>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b-2 border-current">
                {isApproved ? <CheckCircle className="w-6 h-6 text-green-600" /> :
                  isRejected ? <XCircle className="w-6 h-6 text-red-600" /> :
                  isPendingReview ? <Clock className="w-6 h-6 text-yellow-600" /> : null}
                <h4 className="text-base font-bold text-gray-800 uppercase">
                  {isApproved ? 'Approved' : isRejected ? 'Rejected' : isPendingReview ? 'Set to Pending Review' : 'Status'}
                </h4>
              </div>
              <div className="flex gap-6">
                <div className="flex-1 space-y-4">
                  <div>
                    <p className="text-xs text-gray-600 uppercase font-semibold mb-1">
                      {isApproved ? 'Approved By' : isRejected ? 'Rejected By' : isPendingReview ? 'Set by' : 'Action By'}
                    </p>
                    <p className="text-base font-bold text-gray-900">
                      {approverDetails ? `${approverDetails.firstName} ${approverDetails.lastName}` : `User ${poApproval.approverId}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase font-semibold mb-1">Date</p>
                    <p className="text-sm font-medium text-gray-800">{formatDate(poApproval.responseDateTime)}</p>
                  </div>
                  {poApproval.responseRemarks && (
                    <div>
                      <p className="text-xs text-gray-600 uppercase font-semibold mb-1">Remarks</p>
                      <p className="text-sm text-gray-800 italic">"{poApproval.responseRemarks}"</p>
                    </div>
                  )}
                </div>
                <div className="w-80">
                  <p className="text-xs text-gray-600 uppercase font-semibold mb-2 text-center">Digital Signature</p>
                  <div className={`bg-white border-2 border-dashed rounded-lg p-3 h-32 flex items-center justify-center ${
                    isApproved ? 'border-green-400' : isRejected ? 'border-red-400' : isPendingReview ? 'border-yellow-400' : 'border-gray-400'
                  }`}>
                    {approverSignature ? (
                      <img src={approverSignature} alt="Digital Signature" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <div className="text-center text-gray-400">
                        <FileText className="w-8 h-8 mx-auto mb-1 opacity-50" />
                        <p className="text-xs">No signature</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ✅ ACTION BUTTONS - Visible ONLY if pending and NOT yet approved/rejected */}
          {showActionButtons && (
            <>
              {!canApprovePO && !isCheckingPermissions && (
                <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-400 rounded-full p-2"><XCircle className="w-6 h-6 text-red-900" /></div>
                    <div className="flex-1">
                      <h4 className="font-bold text-red-900 text-lg">No Permission</h4>
                      <p className="text-red-800 text-sm">Your position does not have permission to approve Purchase Orders.</p>
                    </div>
                  </div>
                </div>
              )}
              {canApprovePO && (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-400 rounded-full p-2"><AlertCircle className="w-6 h-6 text-yellow-900" /></div>
                    <div className="flex-1">
                      <h4 className="font-bold text-yellow-900 text-lg">For Review</h4>
                      <p className="text-yellow-800 text-sm">This Purchase Order is pending your approval decision.</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <button onClick={handleApprove} disabled={isButtonDisabled}
                  className={`py-4 rounded-lg font-bold text-base flex items-center justify-center gap-3 shadow-lg transition-all transform ${
                    isButtonDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 hover:shadow-xl hover:scale-[1.02]'
                  } text-white`}>
                  <CheckCircle className="w-6 h-6" />
                  {isCheckingPermissions ? 'Checking...' : isSubmitting ? 'Processing...' : 'Approve'}
                </button>
                <button onClick={handleSetPending} disabled={isButtonDisabled}
                  className={`py-4 rounded-lg font-bold text-base flex items-center justify-center gap-3 shadow-lg transition-all transform ${
                    isButtonDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700 hover:shadow-xl hover:scale-[1.02]'
                  } text-white`}>
                  <Clock className="w-6 h-6" />
                  {isCheckingPermissions ? 'Checking...' : isSubmitting ? 'Processing...' : 'Set Pending'}
                </button>
                <button onClick={handleReject} disabled={isButtonDisabled}
                  className={`py-4 rounded-lg font-bold text-base flex items-center justify-center gap-3 shadow-lg transition-all transform ${
                    isButtonDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:shadow-xl hover:scale-[1.02]'
                  } text-white`}>
                  <XCircle className="w-6 h-6" />
                  {isCheckingPermissions ? 'Checking...' : isSubmitting ? 'Processing...' : 'Reject'}
                </button>
              </div>
            </>
          )}

          {/* ✅ CLOSE BUTTON - Laging visible */}
          <div className="flex justify-end">
            <button onClick={onClose}
              className="px-8 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-all">
              Close
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default POApprovalModal;